import { useCallback, useEffect, useMemo, useState } from 'react'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'
import moment from 'moment'

import {
  getOrgEventsForReport,
  getRevenueComparisonPreview,
  getRevenueInsights,
  runRevenueComparison,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { formatNumber } from '../../utilities/helpers'
import { EventSelector } from '../EventSelector'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'

import './revenueInsightsWrapper.scss'

const METRIC_ROWS = [
  { key: 'sold_percentage', label: 'Sell-through %', suffix: '%', decimals: 1 },
  { key: 'tickets_sold', label: 'Tickets sold', decimals: 0 },
  { key: 'gross_revenue', label: 'Gross revenue', prefix: '$', decimals: 0 },
  { key: 'net_revenue', label: 'Net revenue', prefix: '$', decimals: 0 },
]

function formatMetricValue(value, { prefix = '', suffix = '', decimals = 0 }) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  const formatted =
    decimals > 0 ? n.toFixed(decimals) : formatNumber(Math.round(n))
  return `${prefix}${formatted}${suffix}`
}

function severityClass(severity) {
  const s = String(severity || 'medium').toLowerCase()
  if (s === 'high') return 'severity-high'
  if (s === 'low') return 'severity-low'
  return 'severity-medium'
}

function reasonMessage(reason, comparableCount, minComparables) {
  switch (reason) {
    case 'target_snapshot_not_found':
      return 'No daily snapshot exists for this event on the selected date. Run revenue snapshots first (daily cron or backfill).'
    case 'insufficient_comparables':
      return `Not enough similar past events to benchmark (found ${comparableCount ?? 0}, need ${minComparables ?? 2}). Comparables use completed events with the same day-of-week and similar days-until-show.`
    default:
      return reason || 'Comparison could not be completed.'
  }
}

function ComparisonMetricsTable({ target, benchmark, pctDiffs }) {
  if (!target || !benchmark) return null

  return (
    <Table responsive className="revenue-insights-metric-table mb-0">
      <thead>
        <tr>
          <th>Metric</th>
          <th>This event</th>
          <th>Benchmark avg</th>
          <th>vs benchmark</th>
        </tr>
      </thead>
      <tbody>
        {METRIC_ROWS.map(({ key, label, ...fmt }) => {
          const pct = pctDiffs?.[key]
          const pctLabel =
            pct == null || !Number.isFinite(Number(pct))
              ? '—'
              : `${Number(pct) > 0 ? '+' : ''}${Number(pct).toFixed(1)}%`

          return (
            <tr key={key}>
              <td>{label}</td>
              <td>{formatMetricValue(target[key], fmt)}</td>
              <td>{formatMetricValue(benchmark[key], fmt)}</td>
              <td
                className={
                  Number(pct) < -10
                    ? 'text-danger'
                    : Number(pct) > 10
                      ? 'text-success'
                      : ''
                }
              >
                {pctLabel}
              </td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

function InsightCard({ insight }) {
  const rec = insight.recommendations?.[0]

  return (
    <Card
      body
      className={`card--xs card-with-border revenue-insight-card ${severityClass(insight.severity)}`}
    >
      <Stack gap={2}>
        <Stack
          direction="horizontal"
          className="flex-wrap gap-2 align-items-start"
        >
          <h3 className="h6 mb-0 flex-grow-1">{insight.title}</h3>
          <Badge bg="secondary" className="text-capitalize">
            {insight.insight_type?.replace(/_/g, ' ') || 'insight'}
          </Badge>
          <Badge
            bg={
              insight.severity === 'high'
                ? 'danger'
                : insight.severity === 'low'
                  ? 'success'
                  : 'warning'
            }
          >
            {insight.severity || 'medium'}
          </Badge>
          <Badge bg="light" text="dark">
            {insight.status || 'new'}
          </Badge>
        </Stack>
        <p className="mb-0">{insight.summary}</p>
        <p className="revenue-insight-meta text-muted mb-0">
          Actual {insight.metric_value ?? '—'}% vs benchmark{' '}
          {insight.benchmark_value ?? '—'}%
          {insight.impact_amount > 0 && (
            <> · Est. impact ${formatNumber(insight.impact_amount)}</>
          )}
          {insight.confidence_score != null && (
            <> · Confidence {insight.confidence_score}%</>
          )}
        </p>
        {rec && (
          <Card className="bg-light border-0">
            <Card.Body className="py-2 px-3">
              <p className="small fw-semibold mb-1">{rec.title}</p>
              <p className="small mb-0">{rec.description}</p>
            </Card.Body>
          </Card>
        )}
      </Stack>
    </Card>
  )
}

export default function RevenueInsightsWrapper({ organization }) {
  const organizationUUID = organization?.uuid
  const [events, setEvents] = useState([])
  const [selectedEventUUID, setSelectedEventUUID] = useState('')
  const [snapshotDate, setSnapshotDate] = useState(
    moment().format('YYYY-MM-DD')
  )
  const [comparison, setComparison] = useState(null)
  const [insights, setInsights] = useState([])
  const [isLoadingEvents, setIsLoadingEvents] = useState(true)
  const [isLoadingComparison, setIsLoadingComparison] = useState(false)
  const [isLoadingInsights, setIsLoadingInsights] = useState(false)
  const isReportRefreshing = isLoadingComparison || isLoadingInsights
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isReportRefreshing)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saveMessage, setSaveMessage] = useState(null)

  const loadInsights = useCallback(async () => {
    if (!organizationUUID) return
    setIsLoadingInsights(true)
    try {
      const res = await getRevenueInsights(organizationUUID, {
        eventUUID: selectedEventUUID || undefined,
      })
      setInsights(Array.isArray(res?.data?.data) ? res.data.data : [])
    } catch (err) {
      console.error('Failed to load revenue insights', err)
      setInsights([])
    } finally {
      setIsLoadingInsights(false)
      completeRefresh()
    }
  }, [organizationUUID, selectedEventUUID])

  const loadComparisonPreview = useCallback(async () => {
    if (!organizationUUID || !selectedEventUUID) {
      setComparison(null)
      return
    }
    setIsLoadingComparison(true)
    setError(null)
    setSaveMessage(null)
    try {
      const res = await getRevenueComparisonPreview(
        organizationUUID,
        selectedEventUUID,
        snapshotDate
      )
      setComparison(res?.data?.data ?? null)
    } catch (err) {
      console.error('Failed to load comparison', err)
      setComparison(null)
      setError('Could not load comparison for this event.')
    } finally {
      setIsLoadingComparison(false)
      completeRefresh()
    }
  }, [organizationUUID, selectedEventUUID, snapshotDate])

  useEffect(() => {
    if (!organizationUUID) return
    setIsLoadingEvents(true)
    getOrgEventsForReport(organizationUUID)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : []
        const active = list.filter((e) =>
          ['on_sale', 'published', 'complete'].includes(e?.status)
        )
        setEvents(active.length ? active : list)
      })
      .catch((err) => {
        console.error('Failed to load events', err)
        setEvents([])
      })
      .finally(() => setIsLoadingEvents(false))
  }, [organizationUUID])

  useEffect(() => {
    loadInsights()
  }, [loadInsights, refreshToken])

  useEffect(() => {
    loadComparisonPreview()
  }, [loadComparisonPreview, refreshToken])

  const handleRunComparison = async () => {
    if (!organizationUUID || !selectedEventUUID) return
    setIsSaving(true)
    setError(null)
    setSaveMessage(null)
    try {
      const res = await runRevenueComparison(
        organizationUUID,
        selectedEventUUID,
        snapshotDate
      )
      const data = res?.data?.data
      setComparison(data ?? null)
      if (data?.ok && data.insightsGenerated > 0) {
        setSaveMessage(
          `Saved ${data.insightsGenerated} insight${data.insightsGenerated === 1 ? '' : 's'}.`
        )
      } else if (data?.ok) {
        setSaveMessage('Comparison complete — no new insights met thresholds.')
      } else {
        setSaveMessage(null)
      }
      await loadInsights()
    } catch (err) {
      console.error('Failed to run comparison', err)
      setError('Could not generate insights. Try again or check snapshots.')
    } finally {
      setIsSaving(false)
    }
  }

  const filteredInsights = useMemo(() => {
    if (!selectedEventUUID) return insights
    return insights.filter((i) => i.event_uuid === selectedEventUUID)
  }, [insights, selectedEventUUID])

  return (
    <section
      className="spacer-sm revenue-insights-wrapper"
      id="revenue-insights"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Revenue insights</h1>
        </div>
        <p className="section-header-desc">
          Compare on-sale events to similar past shows for your organization
          (same day-of-week and days until event). Generate actionable insights
          and recommendations from daily revenue snapshots.
        </p>
      </header>

      <Card
        body
        className="card--xs card-with-border revenue-insights-controls"
      >
        <Row className="g-3 align-items-end">
          <Col md={5}>
            <EventSelector
              events={events}
              selected={selectedEventUUID}
              setSelected={setSelectedEventUUID}
              isLoading={isLoadingEvents}
              groupByYear
            />
          </Col>
          <Col md={3}>
            <Form.Group controlId="revenue-insight-snapshot-date">
              <Form.Label>Snapshot date</Form.Label>
              <Form.Control
                type="date"
                value={snapshotDate}
                onChange={(e) => setSnapshotDate(e.target.value)}
                disabled={!selectedEventUUID}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Stack direction="horizontal" gap={2} className="flex-wrap">
              <ReportRefreshButton
                onRefresh={refresh}
                isRefreshing={isReportRefreshing}
                disabled={!selectedEventUUID || isReportRefreshing}
              />
              <Button
                variant="primary"
                onClick={handleRunComparison}
                disabled={!selectedEventUUID || isSaving}
              >
                {isSaving ? 'Generating…' : 'Generate & save insights'}
              </Button>
            </Stack>
          </Col>
        </Row>
      </Card>

      {error && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-danger mb-0">{error}</p>
        </Card>
      )}

      {saveMessage && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-success mb-0">{saveMessage}</p>
        </Card>
      )}

      {selectedEventUUID && isLoadingComparison && <PageLoadingContainer />}

      {selectedEventUUID && !isLoadingComparison && comparison && (
        <Card body className="card--xs card-with-border mt-3">
          {!comparison.ok ? (
            <p className="revenue-comparison-empty mb-0">
              {reasonMessage(
                comparison.reason,
                comparison.comparableCount,
                comparison.minComparables
              )}
            </p>
          ) : (
            <Stack gap={3}>
              <Stack direction="horizontal" className="flex-wrap gap-2">
                <span className="text-muted small">
                  Benchmark: {comparison.comparableCount} similar completed
                  events
                </span>
                {comparison.filters?.daysUntilEvent != null && (
                  <span className="text-muted small">
                    · {comparison.filters.daysUntilEvent} days until show
                  </span>
                )}
                {comparison.target?.days_until_event != null && (
                  <span className="text-muted small">
                    · Snapshot sell-through{' '}
                    {Number(comparison.target.sold_percentage).toFixed(1)}%
                  </span>
                )}
              </Stack>
              <ComparisonMetricsTable
                target={comparison.target}
                benchmark={comparison.benchmark}
                pctDiffs={comparison.pctDiffs}
              />
            </Stack>
          )}
        </Card>
      )}

      <Row className="g-3 mt-1">
        <Col>
          <Stack
            direction="horizontal"
            className="justify-content-between mb-2"
          >
            <h2 className="h5 mb-0">Saved insights</h2>
            {isLoadingInsights && (
              <span className="text-muted small">Loading…</span>
            )}
          </Stack>
          {!selectedEventUUID && !isLoadingInsights && insights.length > 0 && (
            <p className="text-muted small">
              Showing insights for all events. Select an event to filter.
            </p>
          )}
          {selectedEventUUID &&
            !isLoadingInsights &&
            filteredInsights.length === 0 && (
              <Card body className="card--xs card-with-border">
                <p className="revenue-comparison-empty mb-0">
                  No insights saved for this event yet. Run a comparison above.
                </p>
              </Card>
            )}
          <Stack gap={2}>
            {filteredInsights.map((insight) => (
              <InsightCard key={insight.id} insight={insight} />
            ))}
          </Stack>
        </Col>
      </Row>
    </section>
  )
}
